<template>
  <CustomTableCanDropDrag
    :draggable="true"
    :columns="tableFields"
    :data="dataRender"
    :is-loading="loading || isLoading"
    :is-loading-scroll-to-end="true"
    :show-checkbox-row="isShowCheckbox"
    :reorderable="false"
    :table-type="DRAG_AND_DROP_TABLE_TYPE.ASSESSMENTTABLE"
    :empty-text="
      getComponentUiText(
        'app-assess-recommendations-assessment-table-empty-message-1',
      )
    "
    :to-sorted="toSorted"
    :selected-children-ids-key="'checklistChildren'"
    :header-wrapper-class="'tw-bg-[#fcfcfd]'"
    :is-global-search="isGlobalSearch()"
    type="assessment-question-table"
    @update-selected-rows="updateSelectedRows($event)"
    @added="addData"
    @scroll-to-end="handleScrollToEnd"
    @clicked-row="onClickRow($event)"
  >
    <template #cell(category)="data">
      <div class="tw-flex tw-w-full tw-gap-[10px] tw-items-center">
        <StatusBadge
          v-if="data.item.checklistItemCategoryLabel"
          :label="data.item.checklistItemCategoryLabel"
          class="tw-w-fit"
          :value="
            data.item.checklistItemIsExcluded
              ? statusCode.disable
              : statusCode.category
          "
        />
        <template v-if="data.item.checklistItem3ndSubCategoryLabel">
          <div>
            <ButtonShowMore
              :id-popover="
                'popover-btn-show-more-assessment-question-table-1-' +
                data.item.checklistItemId
              "
              :is-from-notification="true"
              :classes="'!tw-p-0'"
              :icon-class="'!tw-p-0 !tw-m-0'"
              :dropdown-class="'tw-max-w-max assessment-question-table-item-dropdown'"
            >
              <template #icon>
                <StatusBadge
                  :label="'...'"
                  class="tw-w-fit"
                  :value="
                    data.item.checklistItemIsExcluded
                      ? statusCode.disable
                      : statusCode.subCategory
                  "
                />
              </template>
              <template #default>
                <div class="tw-flex">
                  <div
                    v-for="(text, index) in data.item
                      .checklistItem2ndSubCategoryLabel"
                    :key="text"
                    class="tw-flex tw-items-center tw-justify-center"
                  >
                    <span
                      v-if="index > 0"
                      class="tw-text-[11px] tw-font-medium tw-text-[#387DFF]"
                      >&nbsp;>&nbsp;</span
                    >
                    <div
                      class="tw-flex tw-items-center tw-h-[20px] tw-px-[5px] tw-bg-[#E4F1FF] tw-rounded tw-text-[11px] tw-font-medium tw-text-[#387DFF]"
                    >
                      <span class="tw-max-w-[115px] tw-truncate">
                        {{ text }}
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </ButtonShowMore>
          </div>
          <StatusBadge
            :label="data.item.checklistItem3ndSubCategoryLabel?.[0]"
            class="tw-w-fit"
            :value="
              data.item.checklistItemIsExcluded
                ? statusCode.disable
                : statusCode.subCategory
            "
          />
        </template>
        <template v-else>
          <StatusBadge
            v-if="data.item.checklistItem2ndSubCategoryLabel"
            :label="data.item.checklistItem2ndSubCategoryLabel?.[0]"
            class="tw-w-fit"
            :value="
              data.item.checklistItemIsExcluded
                ? statusCode.disable
                : statusCode.subCategory
            "
          />
        </template>
      </div>
    </template>
    <template #cell(orderNumber)="data">
      <div v-if="inTabOverview" class="tw-flex">
        <div class="title tw-flex tw-items-center tw-gap-[10px]">
          {{ data.item.checklistItemName
          }}<span
            v-if="
              data.item.checklistItemDescription ||
              data.item.checklistItemPerTypeDuplicate
            "
          >
            <ButtonShowDuplicateAssessment
              :id-popover="`popover-assessment-advanced-checklist-item-description-${data.item.checklistItemId}`"
              :custom-class="'tw-w-[max-content]  md:tw-max-w-[350px] lg:tw-max-w-[470px] tw-p-[15px]'"
              :classes="`!tw-leading-none `"
            >
              <div
                class="tw-text-[14px] tw-font-semibold tw-text-[#211f58] tw-font-poppins"
              >
                {{
                  getComponentUiText(
                    "app-menu-submenu-assessments-further-information-1",
                  )
                }}
              </div>
              <CustomDescriptionContent
                v-if="data.item.checklistItemDescription"
                :description="data.item.checklistItemDescription"
                :custom-class="'!tw-p-0'"
              >
              </CustomDescriptionContent>
              <CustomDescriptionContent
                v-if="data.item.checklistItemPerTypeDuplicate"
                :description="`<i>${data.item.checklistItemPerTypeDuplicate}</i>`"
                :custom-class="'!tw-p-0'"
              ></CustomDescriptionContent>
            </ButtonShowDuplicateAssessment>
          </span>
        </div>
      </div>
      <div v-else class="tw-flex tw-gap-[17px] tw-items-center tw-w-fit">
        <template v-if="!inModalEdit">
          <div
            v-if="generateLevelQuestions(data.item.orderNumber) > 0"
            class="tw-flex tw-gap-[17px]"
          >
            <EnterIcon
              v-for="i in Array.from({
                length: generateLevelQuestions(data.item.orderNumber),
              })"
              :key="i"
            />
          </div>
          <span
            :class="
              data.item.checklistItemIsExcluded
                ? 'tw-text-[#DBDBDB]'
                : 'tw-text-[#231F20]'
            "
            class="tw-text-[13px] tw-font-semibold"
          >
            {{ data.item.orderNumber }}
          </span>
        </template>

        <!-- Label category -->
        <div class="tw-flex tw-gap-[10px] tw-items-center tw-min-w-fit">
          <StatusBadge
            v-if="data.item.checklistItemCategoryLabel"
            :label="data.item.checklistItemCategoryLabel"
            class="tw-w-fit"
            :value="
              data.item.checklistItemIsExcluded
                ? statusCode.disable
                : statusCode.category
            "
          />
          <template v-if="data.item.checklistItem3ndSubCategoryLabel">
            <div>
              <ButtonShowMore
                :id-popover="
                  'popover-assessment-question-table-2-' +
                  inTabOverview +
                  isActionTable +
                  data.item.checklistItemId
                "
                :is-from-notification="true"
                :classes="'!tw-p-0'"
                :icon-class="'!tw-p-0 !tw-m-0'"
                :dropdown-class="'tw-max-w-max assessment-question-table-item-dropdown'"
                custom-class="blue-tooltip"
              >
                <template #icon>
                  <StatusBadge
                    :label="'...'"
                    class="tw-w-fit"
                    :value="
                      data.item.checklistItemIsExcluded
                        ? statusCode.disable
                        : statusCode.subCategory
                    "
                  />
                </template>
                <template #default>
                  <div class="tw-flex">
                    <div
                      v-for="(text, index) in data.item
                        .checklistItem2ndSubCategoryLabel"
                      :key="text"
                      class="tw-flex tw-items-center tw-justify-center"
                    >
                      <span
                        v-if="index > 0"
                        class="tw-text-[11px] tw-font-medium tw-text-[#387DFF]"
                        >&nbsp;>&nbsp;</span
                      >
                      <div
                        class="tw-flex tw-items-center tw-h-[20px] tw-px-[5px] tw-bg-[#E4F1FF] tw-rounded tw-text-[11px] tw-font-medium tw-text-[#387DFF]"
                      >
                        <span class="tw-max-w-[115px] tw-truncate">
                          {{ text }}
                        </span>
                      </div>
                    </div>
                  </div>
                </template>
              </ButtonShowMore>
            </div>
            <StatusBadge
              :label="data.item.checklistItem3ndSubCategoryLabel?.[0]"
              class="tw-w-fit"
              :value="
                data.item.checklistItemIsExcluded
                  ? statusCode.disable
                  : statusCode.subCategory
              "
            />
          </template>
          <template v-else>
            <StatusBadge
              v-if="data.item.checklistItem2ndSubCategoryLabel"
              :label="data.item.checklistItem2ndSubCategoryLabel?.[0]"
              class="tw-w-fit"
              :value="
                data.item.checklistItemIsExcluded
                  ? statusCode.disable
                  : statusCode.subCategory
              "
            />
          </template>
        </div>

        <span
          class="tw-text-[13px] tw-font-normal tw-line-clamp-2 tw-min-w-[150px]"
          :class="
            data.item.checklistItemIsExcluded
              ? 'tw-text-[#DBDBDB]'
              : 'tw-text-[#231F20]'
          "
        >
          {{
            data.item.checklistItemHeadingLabel || data.item.checklistItemName
          }}
        </span>
      </div>
    </template>

    <template #cell(assigned)="data">
      <div class="tw-flex tw-justify-center">
        <CheckboxIcon
          v-if="
            !data.item.checklistChildren &&
            checkIsAssigned(data.item.checklistItemId)
          "
        />
        <span v-else></span>
      </div>
    </template>

    <template #cell(checklistItemImportanceId)="data">
      <div class="tw-flex tw-justify-center">
        <Importance
          v-if="!data.item.checklistChildren"
          :class="[
            'tw-w-[12px] tw-h-[12px] ',
            data.item.checklistItemImportanceId
              ? CHECKLIST_ICON_VALUE_WITH_COLOR[
                  data.item.checklistItemImportanceId
                ]
              : '',
          ]"
          :color="
            data.item.checklistItemIsExcluded ? '#DBDBDB' : 'currentColor'
          "
        />
      </div>
    </template>

    <template #cell(itemCompleteValue)="data">
      <div
        :class="`tw-flex tw-justify-center tw-items-center tw-h-[20px] tw-cursor-pointer tw-py-${
          inModalEdit ? 5 : 0
        }`"
      >
        <CircularProgressPercent
          v-if="
            !data.item.checklistChildren &&
            inModalEdit &&
            (data.item.checklistItemCompensatingControlValue !== null ||
              data.item.checklistItemComplianceOverride !== null ||
              data.item.checklistItemComplianceTypeValue !== null)
          "
          :percent="
            (data.item.checklistItemCompensatingControlValue ??
              data.item.checklistItemComplianceOverride ??
              data.item.checklistItemComplianceTypeValue) * 100
          "
          :compensating-override-score="
            data.item.checklistItemCompensatingControlValue
          "
          :compliance-override-score="data.item.checklistItemComplianceOverride"
          :class="'tw-cursor-pointer '"
          @click="handleManageQuestions(data)"
        />
        <div
          v-if="
            !data.item.checklistChildren &&
            !inModalEdit &&
            data.item.itemCompleteValue !== undefined
          "
          @click="handleManageQuestions(data)"
        >
          <div
            class="tw-h-[20px] tw-bg-white tw-flex tw-justify-center tw-rounded tw-border tw-border-solid tw-border-[#EFF0F1] tw-px-[5px]"
          >
            <span
              :class="`tw-text-[${
                data.item.checklistItemIsExcluded ? '#DBDBDB' : '#01893D'
              }] tw-leading-[180%] tw-text-[11px] tw-font-semibold`"
            >
              {{ data.item.itemCompleteValue }}
            </span>
            <span
              :class="`tw-text-[${
                data.item.checklistItemIsExcluded ? '#DBDBDB' : '#01893D'
              }] tw-leading-[180%] tw-text-[8px] tw-font-semibold`"
            >
              %
            </span>
          </div>
        </div>
      </div>
    </template>

    <template #cell(totalScore)="data">
      <div>
        <ScoreIncreaseElement
          v-if="!data.item.checklistChildren"
          :class="['tw-pl-[12px] ']"
          :first-score="data.item.checklistItemScore"
          :total-score="data.item.checklistItemScoreMax"
          :excluded="data.item.checklistItemIsExcluded"
          @open-advanced="handleManageQuestions(data)"
        />
      </div>
    </template>

    <template #cell(action)="data">
      <div>
        <CellTableAction
          v-if="!data.item.checklistChildren"
          :id="
            'overview-questions-table-' +
            inTabOverview +
            isActionTable +
            data.item.checklistItemId
          "
          :value="data"
          :is-internal="false"
          :actions="externalQuestionsActions"
          :in-modal-edit="inModalEdit && isShowDelete"
          @manage-user="
            (value, action, isInternal) => {
              if (action === actionClickType.actionShowAssessmentsAdvanced) {
                handleManageQuestions(data);
              } else if (action === actionClickType.actionAddActionQuestion) {
                handleAddActionQuestion(data);
              } else if (
                action === actionClickType.actionDeleteActionQuestion
              ) {
                deleteActionQuestion(data);
              } else if (action === actionClickType.actionRemoveFormAction) {
                removeFormAction(data);
              }
            }
          "
        />
      </div>
    </template>
  </CustomTableCanDropDrag>
</template>

<script lang="ts" setup>
import { getComponentUiText } from "@/utils/utilities";
import {
  actionClickType,
  CHECKLIST_ICON_VALUE_WITH_COLOR,
  DRAG_AND_DROP_TABLE_TYPE,
  RIGHT_SLIDE_MODAL_NAME,
  routers,
  statusCode,
  THRESH_HOLD_GET_LIST_NUMBER,
} from "@/utils/constants";
import { CustomTableColumn } from "@/components/common/customTable.vue";
import CellTableAction from "@/components/common/setting/cellTableAction.vue";
import Importance from "../svgs/importance.vue";
import {
  IAssessmentChecklistItem,
  IAssessmentQuestionTable,
  IAssessmentQuestionType,
} from "@/interfaces/assessments/IAssessmentDetails";
import ScoreIncreaseElement from "../assessments/scoreIncreaseElement.vue";
import EnterIcon from "../svgs/enterIcon.vue";
import CheckboxIcon from "../svgs/checkboxIcon.vue";
import { computed, ref, Ref, watch } from "vue";
import { IModalMessage } from "@/interfaces/common/common";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import CircularProgressPercent from "@/components/common/circularProgressPercent.vue";
import ButtonShowMore from "@/components/common/setting/buttonShowMore.vue";
import StatusBadge from "@/components/common/setting/statusBadge.vue";
import CustomTableCanDropDrag from "@/components/common/customTableCanDropDrag.vue";
import store from "@/store";
import { isPassedSearch } from "@/services/assessment";
import debounce from "lodash.debounce";
import { useMemoize } from "@vueuse/core";
import router from "@/router";
import { recommendRoute } from "@/services/apiRoutes/routes";
import ButtonShowDuplicateAssessment from "../common/setting/buttonShowDuplicateAssessment.vue";
import CustomDescriptionContent from "../common/customDescriptionContent.vue";

const props = defineProps({
  searchKey: {
    type: String,
    default: () => "",
  },
  tableData: {
    type: Array as () => IAssessmentChecklistItem[],
    default: () => [],
  },
  column: {
    type: Array,
    default: () => [
      "orderNumber",
      "assigned",
      "checklistItemImportanceId",
      "itemCompleteValue",
      "totalScore",
      "action",
    ],
  },
  loading: { type: Boolean, default: true },
  inModalEdit: { type: Boolean, default: false },
  inTabOverview: { type: Boolean, default: false },
  isShowCheckbox: {
    type: Boolean,
    required: false,
    default: true,
  },
  checklistGroupId: {
    type: Number,
    default: 0,
  },
  isActionTable: {
    type: Boolean,
    default: false,
  },
  isHideExcludeItem: {
    type: Boolean,
    default: true,
  },
});

const tableFields = computed<CustomTableColumn[]>(() =>
  [
    {
      text: getComponentUiText(
        "app-assess-recommendations-action-table-overview-title-category-1",
      ),
      value: "category",
      headerClass:
        "tw-text-[#878787] tw-text-[10px] tw-font-semibold tw-tracking-[1px] tw-leading-[100%] tw-uppercase !tw-pr-0",
      valueClass: "xl:tw-w-[320px] tw-w-[200px]",
      sortable: false,
    },
    {
      text: getComponentUiText("app-component-table-header-question-1"),
      value: "orderNumber",
      headerClass:
        "tw-text-[#211F58] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase",
      valueClass: "!tw-py-[20px] tw-w-fit",
      sortable: !props.inModalEdit && !props.inTabOverview,
      sortDefault: true,
    },
    {
      text: getComponentUiText("app-component-table-header-assigned-1"),
      value: "assigned",
      headerClass:
        "tw-justify-center tw-text-[#878787] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase",
      valueClass: "!tw-pr-[12px] !tw-py-[20px]",
      sortable: true,
    },
    {
      text: getComponentUiText("app-component-table-header-importance-1"),
      value: "checklistItemImportanceId",
      headerClass:
        "tw-justify-center tw-text-[#878787] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase",
      valueClass: "!tw-pr-[12px] !tw-py-[20px]",
      sortable: true,
    },
    {
      text: getComponentUiText("app-component-table-header-compliance-1"),
      value: "itemCompleteValue",
      headerClass:
        "tw-justify-center tw-text-[#211F58] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase",
      valueClass: "!tw-pr-[12px] !tw-py-[20px]",
      sortable: true,
    },
    {
      text: getComponentUiText("app-component-table-header-score-increase-1"),
      value: "totalScore",
      headerClass:
        "tw-pl-[12px] tw-text-[#878787] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase",
      valueClass: "!tw-px-[12px] !tw-py-[20px] tw-max-w-[160px] tw-w-[160px]",
      sortable: true,
    },
    {
      text: "",
      value: "action",
      headerClass: "",
      valueClass: "tw-w-[45px] !tw-px-[12px] !tw-py-[20px]",
    },
  ].filter((item) => props.column.includes(item.value)),
);
const isLoading: Ref<boolean> = ref(false);
const debouncedSearchKey = ref(props.searchKey);
watch(
  () => props.searchKey,
  debounce((newValue) => {
    debouncedSearchKey.value = newValue;
  }, 800),
  { immediate: true },
);

watch(
  () => props.searchKey,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      isLoading.value = true;
    }
  },
  { immediate: true },
);

const emit = defineEmits([
  "update-selected-rows",
  "delete-assessment-question",
  "open-modal-create-action",
  "on:open-advanced-slide-modal",
]);

const SortData = ref({
  sortColumnBy: "orderNumber",
  sortDirections: "asc",
});

const selectedList: any = ref([]);
const answerOption: Ref<any> = ref([null]);
const answerOptionsDropDown: Ref<any[]> = ref([]);

const prospectQuestionTypes: Ref<IAssessmentQuestionType[]> = computed(
  () => store.state.Assessment.assessmentQuestionTypes,
);

const isGlobalSearch = () => {
  return window.location.href.includes(routers.assessChecklist);
};

const findAnswer = (answer: any) => {
  answerOption.value = [
    answerOptionsDropDown.value.find((item) => item.value == answer),
  ];
};

const listFilterCheckListGroupTemplate = computed(() => {
  return store.state.Recommend.listFilterCheckListGroupTemplate;
});

const checkIsAssigned = (checklistItemGroupId: number) => {
  return listFilterCheckListGroupTemplate.value.includes(checklistItemGroupId);
};

const getDropdownData = (checklistItemDataRaw: any) => {
  if (
    !checklistItemDataRaw.checklistItemComplianceTypeId ||
    typeof checklistItemDataRaw.checklistItemComplianceTypeId !== "string" ||
    checklistItemDataRaw.checklistItemComplianceTypeId.length === 0
  )
    return;

  const questionType = checklistItemDataRaw.checklistItemComplianceTypeId;
  const data = prospectQuestionTypes.value.find(
    (item) => item.id === questionType,
  );
  if (data) answerOptionsDropDown.value = data.complianceTypeValues;
  else answerOptionsDropDown.value = [];

  answerOptionsDropDown.value = answerOptionsDropDown.value.map(
    (option: any) => {
      const item = {
        ...option,
        name: option.label,
      };
      return item;
    },
  );

  findAnswer(checklistItemDataRaw.checklistItemComplianceTypeValue);
};

const isShowDelete = computed(() => {
  return (
    store.state.Recommend.selectedChecklistAssessmentQuestions.length > 1 ||
    dataRender.value.length > 1
  );
});

const maxScore = ref(THRESH_HOLD_GET_LIST_NUMBER);

const flattenData = (
  array: IAssessmentChecklistItem[],
): IAssessmentQuestionTable[] => {
  const output: IAssessmentQuestionTable[] = [];
  array.forEach((item) => {
    output.push(item as IAssessmentQuestionTable);
    if (item.checklistChildren?.length) {
      output.push(...flattenData(item.checklistChildren));
    }
  });
  return output;
};
const memoizedFlatten = useMemoize(flattenData);

const createItemMap = (
  data: IAssessmentChecklistItem[],
): Map<string, IAssessmentChecklistItem> => {
  const itemMap = new Map<string, IAssessmentChecklistItem>();
  const traverse = (items: IAssessmentChecklistItem[]) => {
    items.forEach((item) => {
      itemMap.set(item.orderNumber, item);
      if (item.checklistChildren?.length) {
        traverse(item.checklistChildren);
      }
    });
  };
  traverse(data);
  return itemMap;
};

const getParentOrderNumbers = (orderNumber: string): string[] => {
  if (!orderNumber.includes(".")) return [];
  const parts = orderNumber.split(".");
  const parents: string[] = [];
  for (let i = 1; i < parts.length; i++) {
    parents.push(parts.slice(0, i).join("."));
  }
  return parents;
};

const newTableData = (): IAssessmentQuestionTable[] => {
  const BG_WHITE = "#fff";
  const isAscending = SortData.value.sortDirections === "asc";

  let data: IAssessmentChecklistItem[] = [...props.tableData];

  const flattenedData = memoizedFlatten(data);

  flattenedData.sort((a, b) => {
    let valueA = a[SortData.value.sortColumnBy];
    let valueB = b[SortData.value.sortColumnBy];
    if (SortData.value.sortColumnBy === "totalScore") {
      valueA =
        +a.checklistItemScoreMax.toFixed(2) - +a.checklistItemScore.toFixed(2);
      valueB =
        +b.checklistItemScoreMax.toFixed(2) - +b.checklistItemScore.toFixed(2);
    }
    if (SortData.value.sortColumnBy === "itemCompleteValue") {
      valueA = valueA === undefined ? -1 : valueA;
      valueB = valueB === undefined ? -1 : valueB;
    }

    if (SortData.value.sortColumnBy === "orderNumber") {
      return isAscending
        ? compareVersion(valueA, valueB)
        : compareVersion(valueB, valueA);
    }
    if (!isNaN(valueA) && !isNaN(valueB)) {
      return isAscending ? valueA - valueB : valueB - valueA;
    }
    if (typeof valueA === "string" && typeof valueB === "string") {
      return isAscending
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    }
    return isAscending ? valueA - valueB : valueB - valueA;
  });

  const itemMap = createItemMap(data);

  let dataSearch = flattenedData.filter((item: IAssessmentQuestionTable) => {
    item.rowBgColor = BG_WHITE;
    const correctSearch = debouncedSearchKey.value
      ? isPassedSearch(item, debouncedSearchKey.value, true)
      : true;
    return (
      (!props.isHideExcludeItem || !item.checklistItemIsExcluded) &&
      correctSearch
    );
  });

  if (debouncedSearchKey.value) {
    const parentsToAdd = new Set<string>();
    const seenOrderNumbers = new Set(
      dataSearch.map((item) => item.orderNumber),
    );

    dataSearch.forEach((item) => {
      const parentOrders = getParentOrderNumbers(item.orderNumber);
      parentOrders.forEach((parentOrder) => {
        if (!seenOrderNumbers.has(parentOrder)) {
          parentsToAdd.add(parentOrder);
        }
      });
    });

    parentsToAdd.forEach((parentOrder) => {
      const parent = itemMap.get(parentOrder);
      if (
        parent &&
        !dataSearch.some((item) => item.orderNumber === parent.orderNumber)
      ) {
        parent.rowBgColor = BG_WHITE;
        dataSearch.push(parent as IAssessmentQuestionTable);
      }
    });

    // dataSearch.sort((a, b) => a.orderNumber.localeCompare(b.orderNumber));e
  }
  // eslint-disable-next-line
  isLoading.value = false;
  return dataSearch;
};

const compareVersion = (a, b) => {
  const partsA = a.split(".").map(Number);
  const partsB = b.split(".").map(Number);
  const maxLen = Math.max(partsA.length, partsB.length);

  for (let i = 0; i < maxLen; i++) {
    const valA = partsA[i] || 0;
    const valB = partsB[i] || 0;

    if (valA !== valB) return valA - valB;
  }

  return 0;
};

const dataRender = computed(() => {
  return newTableData().slice(0, maxScore.value);
});

const addData = (data: any) => {
  EventEmitter.emit(EmitterEvents.ADD_NEW_ASSESSMENT_QUESTION_TO_ACTION, {
    checklistGroupId: props.checklistGroupId,
    element: data.element,
  });
};
const externalQuestionsActions = props.inModalEdit
  ? [actionClickType.actionShowAssessmentsAdvanced]
  : props.inTabOverview && props.isActionTable
    ? [
        actionClickType.actionShowAssessmentsAdvanced,
        actionClickType.actionRemoveFormAction,
      ]
    : props.inTabOverview
      ? [actionClickType.actionShowAssessmentsAdvanced]
      : [
          actionClickType.actionShowAssessmentsAdvanced,
          actionClickType.actionAddActionQuestion,
        ];

const generateLevelQuestions = (orderNumber: string) => {
  if (!orderNumber) return 0;
  return orderNumber.split(".").length - 2;
};
const updateSelectedRows = (selectedRow) => {
  const selectedRowData = newTableData().filter(
    (item) => selectedRow.includes(item.id) && !item.checklistChildren,
  );

  selectedRowData.forEach((item) => {
    delete item.checklistChildren;
  });

  selectedList.value = selectedRow;
  emit("update-selected-rows", selectedRowData);
};

const onClickRow = (data) => {
  if (isGlobalSearch()) {
    handleManageQuestions(data);
  }
};

const handleManageQuestions = (data) => {
  const currentRoute = router.currentRoute?.value.path || "";
  const rawData = data?.item ? { ...data.item } : { ...data };
  getDropdownData(rawData);

  const emitData: IModalMessage = {
    component: RIGHT_SLIDE_MODAL_NAME.ASSESSMENT_ADVANCED,
    properties: {
      showOverlay: currentRoute === recommendRoute.recommend ? true : false,
      isRecommend: true,
      isGlobalSearch: isGlobalSearch(),
      checklistId: rawData.checklistId,
      answerOptionsDropDown: answerOptionsDropDown.value,
      answerOption: answerOption.value,
      checklistItemId: rawData.checklistItemId,
      questionData: rawData,
    },
  };

  emit("on:open-advanced-slide-modal", true);
  EventEmitter.emit(EmitterEvents.OPEN_RIGHT_SLIDE_MODAL, emitData);
};

const handleAddActionQuestion = async (data) => {
  await store.commit("Recommend/SET_SELECTED_ASSESSMENT_QUESTIONS", [
    data.item,
  ]);
  emit("open-modal-create-action");
};

const toSorted = (sortColumnBy: string, sortDirections: string) => {
  SortData.value.sortColumnBy = sortColumnBy;
  SortData.value.sortDirections = sortDirections;
};

const handleScrollToEnd = () => {
  maxScore.value += THRESH_HOLD_GET_LIST_NUMBER;
};

const deleteActionQuestion = (data) => {
  emit("delete-assessment-question", data.item.checklistItemId);
};

const removeFormAction = (data) => {
  const params = {
    checklistItemGroupId: props.checklistGroupId,
    checklistItemIdsToDelete: [data.item.checklistItemId],
  };

  store.dispatch("Recommend/removeChecklistGroup", params);
};
</script>

<style lang="scss" scoped>
:deep(.custom-checkbox) {
  width: 0 !important;
  padding-left: 37px;
}
.tableWrapper {
  height: auto;
  max-height: 687px;
}
</style>

<style lang="scss">
.assessment-question-table-item-dropdown * {
  background-color: #e4f1ff !important;
  border-radius: 4.8px !important;
}
.assessment-question-table-item-dropdown-disable * {
  background-color: #dbdbdb !important;
  border-radius: 4.8px !important;
}
</style>
